import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import store from "@/store";
import { updateChannel } from "@/features/channelSlice";
import { ensureChannelsLoaded } from "@/ts/utils";
import { Platforms } from "@/ts/common-types";

/**
 * !Note[DND]:
 * 1. Custom hook to initialize channels when navigating to activation routes
 * 2. This ensures channel data is loaded whenever user accesses any activation module
 * 3. Optimization: Only runs full logic when entering activation for the first time
 *    or when channels need revalidation (e.g., after client change)
 * 4. Works in conjunction with updateUserAccess() in utils.ts which handles client switching
 * 
 * TODO: @vaheed For modules that are depended on the revstack channel list and are part of activation for them we have to add exclusion in this code
 * ! Modules such as Media Overview, Navigator Overview etc. 
 */

const useActivationChannelInit = () => {
    const location = useLocation();
    const hasInitialized = useRef(false);
    const previousClientId = useRef<number | null>(null);

    useEffect(() => {
        const initializeChannels = async () => {
            const isActivationRoute = location.pathname.includes(`/${Platforms.ACTIVATION}`);
            
            if (!isActivationRoute) {
                // Reset when leaving activation routes
                hasInitialized.current = false;
                return;
            }

            const currentClientId = store.getState().activeClientId.value;
            const clientChanged = previousClientId.current !== null && 
                                 previousClientId.current !== currentClientId;
            
            
            const channelListState = store.getState().channelList;
            const isLoading = channelListState.isLoading;
            
            if ((hasInitialized.current && !clientChanged) || isLoading) {
                return;
            }

            try {
                const { channelId } = await ensureChannelsLoaded(clientChanged);
                
                const currentChannelId = store.getState().channel.value;
                if (channelId !== currentChannelId) {
                    store.dispatch(updateChannel(channelId));
                }

                hasInitialized.current = true;
                previousClientId.current = currentClientId;
            } catch (error) {
                console.error("Error initializing channels:", error);
                alert("Channels not found, please refresh the page!");
            }
        };

        initializeChannels();
    }, [location.pathname]);
};


export default useActivationChannelInit;
